import React, { useEffect, useMemo, useRef, useState } from "react";
import { renderToString } from "react-dom/server";
import { MapContainer, GeoJSON, TileLayer, useMap, ZoomControl } from "react-leaflet";
import { LatLngBoundsExpression, Map, PopupEvent } from "leaflet";
import cn from "classnames";
import { MapStyle } from "../../types/mapTypes";
import { MineProperties } from "../../types/mineTypes";
import { Filter, MinesDataFilter } from "./filter";
import { MapPopup } from "./mapPopup";
import {
  addMineToViewed,
  changeStoredMapStyle,
  getBounds,
  getMapStyle,
  getViewedMines,
  prepareMinesData,
} from "../../helpers/mapHelpers";
import { DEFAULT_MINE_COLOR, FILTERED_MINE_COLOR, VIEWED_MINE_COLOR } from "../../constants/constants";
import { ReactComponent as FiltersIcon } from "../../assets/icons/bars.svg";
import { ReactComponent as MapIcon } from "../../assets/icons/map.svg";
import { ReactComponent as SatelliteIcon } from "../../assets/icons/satellite.svg";
import styles from "./home.module.css";

export interface FeatureInterface {
  properties: MineProperties;
  geometry: {
    coordinates: Array<Array<Array<[number, number]>>>;
  };
}

export interface MinesDataInterface {
  features: FeatureInterface[];
}

const CLOSE_POPUP_BUTTON_ID = "close-map-popup";
export const POPUP_CONTAINER_ID = "popup-container-id";

export const Home = () => {
  const ref = useRef<any>(null);
  const [loading, setLoading] = useState<boolean>(true);

  const [minesData, setMinesData] = useState<MinesDataInterface>();
  const [regionBorder, setRegionBorder] = useState<any>();
  const [geoJsonKey, setGeoJsonKey] = useState<boolean>(true);
  const [viewedMines, setViewedMines] = useState<string[]>(getViewedMines());

  const [filtersOpen, setFiltersOpen] = useState<boolean>(false);
  const [filtered, setFiltered] = useState<boolean>(false);

  const [mapHeight, setMapHeight] = useState<number>(window.innerHeight);
  const [minesDataFilter, setMinesDataFilter] = useState<MinesDataFilter>();
  const [bounds, setBounds] = useState<LatLngBoundsExpression>();
  const [mapStyle, setMapStyle] = useState<MapStyle>(getMapStyle());

  useEffect(() => {
    const handler = () => {
      setMapHeight(window.innerHeight);
    };
    window.addEventListener("resize", handler);
    return () => {
      window.removeEventListener("resize", handler);
    };
  }, []);

  useEffect(() => {
    fetchData().finally(() => {
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    const map = ref.current;
    if (!map) {
      return;
    }

    return listenPopupOpen(map);
  }, [ref.current]);

  const getMineId = (e: PopupEvent) => {
    const el = e.popup.getElement();
    const container = el?.querySelector(`#${POPUP_CONTAINER_ID}`);

    return container ? container.getAttribute("data-feature-id") : null;
  };

  const listenPopupOpen = (map: Map) => {
    const popupOpenHandler = (e: PopupEvent) => {
      const el = document.querySelector(`#${CLOSE_POPUP_BUTTON_ID}`);
      if (!el) {
        return;
      }
      const handler = () => {
        map.closePopup();
      };
      el.addEventListener("click", handler);
      const popupCloseHandler = () => {
        addMineToViewed(getMineId(e));
        setViewedMines(getViewedMines());

        map.off("popupclose", popupCloseHandler);
        el.removeEventListener("click", handler);
      };
      map.on("popupclose", popupCloseHandler);
    };

    map.on("popupopen", popupOpenHandler);
    return () => {
      map.off("popupopen", popupOpenHandler);
    };
  };

  const fetchData = async () => {
    const minesData = await fetch("/mines.geojson");
    const minesJsonData = await minesData.json();

    const regionData = await fetch("/regionBorderLine.geojson");
    const regionJsonData = await regionData.json();

    setMinesData(prepareMinesData(minesJsonData));
    setRegionBorder(regionJsonData);
  };

  const mineTypes = useMemo(() => {
    if (!minesData) {
      return [];
    }
    const mineTypesDct: Record<string, number> = {}; /* { [key - mineType]: number - count mines } */

    minesData.features.forEach(({ properties }) => {
      if (!properties.Mine_Type) {
        return;
      }
      if (!mineTypesDct[properties.Mine_Type]) {
        mineTypesDct[properties.Mine_Type] = 0;
      }

      mineTypesDct[properties.Mine_Type] += 1;
    });

    return Object.entries(mineTypesDct).map(([mine, count]) => ({ mine, count }));
  }, [minesData]);

  const filteredMinesData = useMemo(() => {
    if (!minesData) {
      return;
    }

    const result = minesDataFilter ? minesDataFilter(minesData) : minesData;

    const newBounds = getBounds(result);
    if (newBounds) {
      setBounds([
        [newBounds.southWest.lng, newBounds.southWest.lat],
        [newBounds.northEast.lng, newBounds.northEast.lat],
      ]);
    }

    return result;
  }, [minesData, minesDataFilter]);

  const otherMinesData = useMemo(() => {
    if (!filteredMinesData || !minesData) {
      return minesData;
    }

    const filteredIds = filteredMinesData.features.map((feature) => feature.properties.id);

    return {
      ...minesData,
      features: minesData.features.filter((feature) => !filteredIds.includes(feature.properties.id)),
    };
  }, [filteredMinesData, minesData]);

  const filtersOnClose = () => {
    setFiltersOpen(false);
  };

  const openFilters = () => {
    setFiltersOpen(true);
  };

  const changeMapStyle = () => {
    const newMapStyle = mapStyle === "normal" ? "satellite" : "normal";
    setMapStyle(newMapStyle);
    changeStoredMapStyle(newMapStyle);
  };

  const getMineFeatureStyle = (feature: FeatureInterface): any => {
    const filteredIds = filteredMinesData
      ? filteredMinesData.features.map((feature) => {
          return feature.properties.id;
        })
      : [];

    const filteredResult = filteredIds.includes(feature.properties.id) && filtered;
    const viewed = viewedMines.includes(feature.properties.id);

    let color = DEFAULT_MINE_COLOR;
    if (filteredResult) {
      color = FILTERED_MINE_COLOR;
    } else {
      color = viewed ? VIEWED_MINE_COLOR : DEFAULT_MINE_COLOR;
    }

    return { color, weight: 7 };
  };

  return (
    <div className={styles.wrapper}>
      {loading && (
        <div className={styles.loaderMask}>
          <div className={styles.loaderText}>Loading...</div>
        </div>
      )}
      <div className={styles.container}>
        <button onClick={openFilters} className={cn(styles.mapButton, styles.filtersButton)}>
          <FiltersIcon className={styles.mapButtonIcon} />
        </button>
        <div className={cn(styles.filters, !filtersOpen && styles.filtersClose)}>
          <Filter
            loading={loading}
            mineTypes={mineTypes}
            onFiltered={setFiltered}
            onClose={filtersOnClose}
            onFilterFunction={(filter) => {
              setMinesDataFilter(() => {
                return filter;
              });
              setGeoJsonKey((prev) => !prev);
            }}
          />
        </div>
        <div className={styles.map}>
          <MapContainer
            ref={ref}
            style={{ height: mapHeight }}
            center={{ lat: 41.1290213474951, lng: 85.49615915672521 }}
            zoom={5}
            scrollWheelZoom={true}
            zoomControl={false}
          >
            {filteredMinesData && regionBorder && (
              <>
                <GeoJSON
                  key={`${String(geoJsonKey)}-border`}
                  data={regionBorder}
                  style={() => {
                    return { color: "#122945", fillColor: "#ffffff", weight: 2 };
                  }}
                />
                <GeoJSON
                  onEachFeature={(feature, layer) => {
                    const popupContent = renderToString(
                      <MapPopup
                        feature={feature}
                        closeButtonId={CLOSE_POPUP_BUTTON_ID}
                        featureId={feature.properties.id}
                      />
                    );
                    layer.bindPopup(popupContent);
                  }}
                  key={`${String(geoJsonKey)}-other`}
                  data={otherMinesData as any}
                  style={(feature) => {
                    // @ts-ignore
                    return getMineFeatureStyle(feature);
                  }}
                />
                <GeoJSON
                  onEachFeature={(feature, layer) => {
                    const popupContent = renderToString(
                      <MapPopup
                        feature={feature}
                        closeButtonId={CLOSE_POPUP_BUTTON_ID}
                        featureId={feature.properties.id}
                      />
                    );
                    layer.bindPopup(popupContent);
                  }}
                  key={`${String(geoJsonKey)}-filtered`}
                  data={filteredMinesData as any}
                  style={(feature) => {
                    // @ts-ignore
                    return getMineFeatureStyle(feature);
                  }}
                />
              </>
            )}
            {/*
            <BoundsManager bounds={bounds} />
            */}
             {/*
             // previus url
             <TileLayer attribution="" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
             */}
            {mapStyle === "normal" ? (
              <TileLayer attribution='&copy; <a href="https://www.mapbox.com/contribute/">Mapbox</a>  &copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>' url={`https://api.mapbox.com/styles/v1/mapbox/light-v10/tiles/{z}/{x}/{y}?access_token=${process.env.REACT_APP_MAP_BOX_ACCESS_TOKEN}`} />
            ) : (
              <TileLayer attribution="" url="https://mt3.google.com/vt/lyrs=s&x={x}&y={y}&z={z}" maxZoom={20} />
            )}

            <ZoomControl position="topright" />
          </MapContainer>
          <button
            onClick={changeMapStyle}
            className={cn(styles.mapButton, styles.mapControlButton, styles.bottomRightMapButton)}
          >
            {mapStyle === "normal" ? (
              <MapIcon className={styles.mapButtonIcon} />
            ) : (
              <SatelliteIcon className={styles.mapButtonIcon} />
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const BoundsManager = ({ bounds }: { bounds?: LatLngBoundsExpression }) => {
  const map = useMap();

  useEffect(() => {
    if (bounds) {
      map.fitBounds(bounds);
    }
  }, [bounds]);

  return null;
};
